import React from "react"

import { ErrorSeverity, PluginError } from "../../../recoil/pluginStudio/types"

interface ErrorDisplayProps {
  errors: PluginError[]
  className?: string
  title?: string
  onDismiss?: (error: PluginError) => void
}

/**
 * A reusable component for displaying PluginError objects
 * with appropriate styling based on severity
 */
const ErrorDisplay: React.FC<ErrorDisplayProps> = ({
  errors,
  className = "",
  title,
  onDismiss,
}) => {
  if (!errors || errors.length === 0) return null

  // Get background color based on severity
  const getBgColor = (severity: ErrorSeverity) => {
    switch (severity) {
      case ErrorSeverity.CRITICAL:
        return "bg-red-900/80"
      case ErrorSeverity.ERROR:
        return "bg-red-800/70"
      case ErrorSeverity.WARNING:
        return "bg-yellow-800/70"
      case ErrorSeverity.INFO:
        return "bg-blue-800/70"
      default:
        return "bg-gray-800/70"
    }
  }

  // Get icon based on severity
  const getIcon = (severity: ErrorSeverity) => {
    switch (severity) {
      case ErrorSeverity.CRITICAL:
      case ErrorSeverity.ERROR:
        return (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-4 w-4 mr-1"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
              clipRule="evenodd"
            />
          </svg>
        )
      case ErrorSeverity.WARNING:
        return (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-4 w-4 mr-1"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
              clipRule="evenodd"
            />
          </svg>
        )
      case ErrorSeverity.INFO:
        return (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-4 w-4 mr-1"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zm-1 4a1 1 0 00-1 1v3a1 1 0 102 0v-3a1 1 0 00-1-1z"
              clipRule="evenodd"
            />
          </svg>
        )
      default:
        return null
    }
  }

  return (
    <div
      className={`rounded-md text-white text-sm p-0 overflow-hidden ${className}`}
    >
      {title && (
        <div className="bg-gray-900 p-2 font-medium border-b border-gray-700">
          {title}
        </div>
      )}
      {errors.map((error, index) => (
        <div
          key={`error-${index}-${error.timestamp}`}
          className={`${getBgColor(error.severity)} p-3 mb-1 last:mb-0 flex items-start`}
        >
          <div className="flex-1">
            <h4 className="font-medium mb-1 flex items-center">
              {getIcon(error.severity)}
              {error.type.charAt(0).toUpperCase() + error.type.slice(1)} Issue
            </h4>
            <p className="text-sm">{error.message}</p>
            {error.details && (
              <pre className="mt-2 text-xs bg-black/30 p-2 rounded overflow-auto max-h-32">
                {JSON.stringify(error.details, null, 2)}
              </pre>
            )}
          </div>
          {onDismiss && (
            <button
              className="ml-2 text-white/70 hover:text-white"
              onClick={() => onDismiss(error)}
              aria-label="Dismiss error"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-4 w-4"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                  clipRule="evenodd"
                />
              </svg>
            </button>
          )}
        </div>
      ))}
    </div>
  )
}

export default ErrorDisplay
